import React, { useState } from 'react';

interface DashboardNavSubmenuProps {
  label: string;
  userRole?: string;
}

const SUBMENU_ITEMS = [
  { icon: '📋', label: 'Registration', path: '/dashboard/registration', minRole: 'Delegate' },
  { icon: '✅', label: 'Check-In', path: '/dashboard/check-in', minRole: 'Delegate' },
  { icon: '📅', label: 'Events', path: '/dashboard/events', minRole: 'Delegate' },
  { icon: '🛎️', label: 'Guest Services', path: '/dashboard/guest-services', minRole: 'Delegate' },
  { icon: '🎭', label: 'Legacy Ball', path: '/dashboard/legacy-ball', minRole: 'Delegate' },
  { icon: '🌍', label: 'Reunion Weekend', path: '/dashboard/reunion-weekend' },
];

const ROLE_LEVELS: Record<string, number> = { Attendee: 1, Delegate: 2, Organizer: 3, Admin: 4 };

/**
 * Expandable submenu in the sidebar that provides quick access
 * to the public dashboard areas. Items with minRole are hidden for lower roles.
 */
export function DashboardNavSubmenu({ label, userRole }: DashboardNavSubmenuProps) {
  const [expanded, setExpanded] = useState(false);
  const userLevel = ROLE_LEVELS[userRole || ''] || 0;
  const visibleItems = SUBMENU_ITEMS.filter((item) => {
    if (!item.minRole) return true;
    return userLevel >= (ROLE_LEVELS[item.minRole] || 0);
  });

  return (
    <div style={styles.container}>
      <button
        style={styles.header}
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <span style={styles.headerIcon}>🎉</span>
        <span style={styles.headerLabel}>{label}</span>
        <span style={styles.arrow}>{expanded ? '▾' : '▸'}</span>
      </button>

      {expanded && (
        <div style={styles.submenu}>
          {visibleItems.map((item) => (
            <a
              key={item.path}
              href={item.path}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.submenuItem}
            >
              <span style={styles.itemIcon}>{item.icon}</span>
              <span style={styles.itemLabel}>{item.label}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    marginTop: '8px',
    borderTop: '1px solid rgba(255,255,255,0.08)',
    paddingTop: '8px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: 'none',
    background: 'linear-gradient(135deg, rgba(26,107,60,0.15), rgba(201,168,76,0.08))',
    color: '#c9a84c',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'left' as const,
  },
  headerIcon: {
    fontSize: '18px',
    width: '24px',
    textAlign: 'center' as const,
  },
  headerLabel: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  arrow: {
    fontSize: '12px',
    color: '#6b8572',
  },
  submenu: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
    marginTop: '4px',
    paddingLeft: '12px',
  },
  submenuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px',
    borderRadius: '6px',
    color: '#a3b8a9',
    fontSize: '13px',
    fontWeight: 500,
    textDecoration: 'none',
    transition: 'all 0.2s',
  },
  itemIcon: {
    fontSize: '14px',
    width: '20px',
    textAlign: 'center' as const,
  },
  itemLabel: {
    flex: 1,
  },
};
